import { Provider } from "react-redux"
import { AppStateProvider } from "../utils/AppStateProvider"
import { WebsocketProvider } from "../common/utils/websocketProvider"
import { CommonComponentContextProvider } from "../utils/CommonContextProvider"
import { BroadcastProvider } from "../common/utils/BroadcastProvider"
import { MessageDisplay } from "../common/components/MessageDisplay"
import { store } from "../store"
import { NetworkStatusMonitor } from "../common/hooks/initHook"
import TopErrorBoundary from "../common/components/TopErrorBoundary"
import { AppProvider } from "../common/components/AppProvider"
import { FormAutofillProvider } from "../common/hooks/autofillHook"

/**
 * Root context provider component that sets up the core application infrastructure.
 * Wraps child components with essential providers and services including:
 * - Redux store for state management
 * - App state management
 * - WebSocket connection handling
 * - Initialization status monitoring
 * - Common component context
 * - Broadcast messaging system
 * - Top-level error boundary
 * - Message display system
 * 
 * This component should be used at the root level of the application to ensure
 * all child components have access to these core contexts and services.
 * 
 * @param {ReactNode} children - Child components to be wrapped with the providers
 * @returns {JSX.Element} The wrapped component tree with all providers
 */
export const OutterRootContext = ({ children }) => {
    return (
        <Provider store={store}>
            <AppStateProvider>
                <NetworkStatusMonitor>
                    <FormAutofillProvider>
                        <BroadcastProvider>
                            <AppProvider>
                                <TopErrorBoundary>
                                    {children}
                                </TopErrorBoundary>
                            </AppProvider>
                        </BroadcastProvider>
                    </FormAutofillProvider>
                </NetworkStatusMonitor>
            </AppStateProvider>
        </Provider>
    )
}

export const InnerRootContext = ({ children }) => {
    return <MessageDisplay>
        <WebsocketProvider>
            <CommonComponentContextProvider>
                {children}
            </CommonComponentContextProvider>
        </WebsocketProvider>
    </MessageDisplay>
}